import type { Locale } from './config';

export type Dictionary = {
  nav: {
    home: string;
    services: string;
    team: string;
    news: string;
    faqs: string;
    contact: string;
    request: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    stats: { years: string; cases: string; areas: string };
    badge1Title: string;
    badge1Sub: string;
    badge2Title: string;
    badge2Sub: string;
    tag1: string;
    tag2: string;
    tag3: string;
  };
  services: {
    title: string;
    subtitle: string;
    learnMore: string;
    sectionTitle: string;
  };
  team: {
    title: string;
    subtitle: string;
    sectionTitle: string;
    viewBio: string;
  };
  news: {
    title: string;
    subtitle: string;
    readMore: string;
    by: string;
    sectionTitle: string;
  };
  faqs: {
    title: string;
    subtitle: string;
  };
  contact: {
    title: string;
    subtitle: string;
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
    submit: string;
    success: string;
    error: string;
    address: string;
    sendUs: string;
    sectionTitle: string;
    judicialNote: string;
  };
  request: {
    title: string;
    subtitle: string;
    requesterName: string;
    institution: string;
    courtCase: string;
    requestType: string;
    requestTypes: { voice: string; audio: string; video: string; linguistic: string; face: string; other: string };
    email: string;
    phone: string;
    message: string;
    submit: string;
    success: string;
    error: string;
    confidential: string;
    confidentialDesc: string;
    features: string[];
  };
  footer: {
    rights: string;
    tagline: string;
    quickLinks: string;
    contactUs: string;
    followUs: string;
    standards: string;
  };
  common: {
    back: string;
    loading: string;
    published: string;
    backToNews: string;
    notFound: string;
  };
  a11y: {
    skipToContent: string;
    logoHome: string;
    openMenu: string;
    closeMenu: string;
    selectLanguage: string;
    backToTop: string;
    whatsapp: string;
    whatsappPrefill: string;
  };
  offline: {
    title: string;
    message: string;
    hint: string;
  };
  brand: {
    fullName: string;
    shortDescription: string;
    manifesto: string;
    etymology: string;
  };
};

export const dictionaries: Record<Locale, Dictionary> = {
  es: {
    nav: {
      home: 'Inicio',
      services: 'Áreas',
      team: 'Equipo',
      news: 'Noticias',
      faqs: 'Preguntas',
      contact: 'Contacto',
      request: 'Solicitar pericia',
    },
    hero: {
      eyebrow: 'Asunción, Paraguay · Biometría Vocal Forense',
      title: 'Identificación Humana Forense',
      subtitle:
        'Combinamos ciencia, tecnología y precisión para identificar personas a través de su voz, habla, lenguaje y rostro. Confiabilidad bajo estándares internacionales (IAFPA, ENFSI, NIST)',
      ctaPrimary: 'Solicitar pericia',
      ctaSecondary: 'Conocer áreas',
      stats: { years: 'años de pericia forense', cases: 'pericias realizadas', areas: 'áreas de especialidad' },
      badge1Title: 'Análisis acústico forense',
      badge1Sub: 'Comparación de locutores',
      badge2Title: 'Detección de voz clonada por IA',
      badge2Sub: 'Análisis espectral avanzado',
      tag1: 'Biometría vocal',
      tag2: 'Pericia forense',
      tag3: 'Detección de deepfakes',
    },
    services: {
      title: 'Áreas de trabajo',
      subtitle: 'Cuatro líneas integradas de identificación humana forense: voz, habla, lenguaje y rostro',
      learnMore: 'Ver detalle',
      sectionTitle: '01',
    },
    team: {
      title: 'Equipo de peritos',
      subtitle: 'Especialistas en fonética forense, lingüística forense, tecnología y biometría facial',
      sectionTitle: '02',
      viewBio: 'Ver biografía',
    },
    news: {
      title: 'Noticias y eventos',
      subtitle: 'Publicaciones de investigación, novedades institucionales y actividades académicas',
      readMore: 'Leer más',
      by: 'Por',
      sectionTitle: '03',
    },
    faqs: {
      title: 'Preguntas frecuentes',
      subtitle: 'Respuestas sobre nuestros procesos y servicios',
    },
    contact: {
      title: 'Contacto y solicitud de pericias',
      subtitle: 'Complete el formulario y un especialista le responderá lo antes posible',
      name: 'Nombre completo',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      subject: 'Asunto',
      message: 'Mensaje',
      submit: 'Enviar mensaje',
      success: 'Su mensaje se ha enviado correctamente',
      error: 'No hemos podido enviar el mensaje. Inténtelo de nuevo',
      address: 'Dirección',
      sendUs: 'Correo electrónico',
      sectionTitle: '04',
      judicialNote: 'Las solicitudes de pericia forense deben adjuntar el auto judicial correspondiente. Plazo de respuesta: 5 días hábiles',
    },
    request: {
      title: 'Solicitud de pericia de voz',
      subtitle: 'Formulario para instituciones judiciales, despachos jurídicos y particulares',
      requesterName: 'Nombre del solicitante',
      institution: 'Institución / Juzgado',
      courtCase: 'Expediente / Carátula',
      requestType: 'Tipo de solicitud',
      requestTypes: {
        voice: 'Identificación forense de voz',
        audio: 'Análisis de audio',
        video: 'Pericia de audio y vídeo',
        linguistic: 'Análisis de habla y lenguaje',
        face: 'Identificación facial forense',
        other: 'Otra',
      },
      email: 'Correo electrónico',
      phone: 'Teléfono',
      message: 'Descripción del caso',
      submit: 'Enviar solicitud',
      success: 'Solicitud recibida. Un especialista se pondrá en contacto con usted en breve',
      error: 'No hemos podido procesar la solicitud',
      confidential: 'Confidencial',
      confidentialDesc: 'La información remitida se trata con confidencialidad profesional y cadena de custodia. Un especialista responsable se pondrá en contacto para coordinar el proceso',
      features: [
        'Trazabilidad documental completa',
        'Informe pericial firmado digitalmente',
        'Metodología conforme a estándares internacionales',
      ],
    },
    footer: {
      rights: 'Todos los derechos reservados',
      tagline: 'Voz · Habla · Lenguaje · Rostro',
      quickLinks: 'Áreas',
      contactUs: 'Contacto',
      followUs: 'Redes',
      standards: 'Estándares',
    },
    common: { back: 'Volver', loading: 'Cargando…', published: 'Publicado', backToNews: 'Volver a noticias', notFound: 'No encontrado' },
    a11y: {
      skipToContent: 'Saltar al contenido',
      logoHome: 'VPS Laboratorio · Ir a inicio',
      openMenu: 'Abrir menú',
      closeMenu: 'Cerrar menú',
      selectLanguage: 'Seleccionar idioma',
      backToTop: 'Volver arriba',
      whatsapp: 'Contactar por WhatsApp',
      whatsappPrefill: 'Hola, quisiera información sobre los servicios de VPS Laboratorio',
    },
    offline: {
      title: 'Sin conexión',
      message: 'No hemos podido cargar esta página. Compruebe su conexión a internet e inténtelo de nuevo',
      hint: 'Las páginas ya visitadas siguen disponibles',
    },
    brand: {
      fullName: 'VPS Laboratorio · Biometría Vocal e Identificación Humana Forense',
      shortDescription: 'Identificación humana forense por voz, habla, lenguaje y rostro',
      manifesto: 'La ciencia que escucha, analiza y revela la verdad',
      etymology: 'Fonética: del griego φωνητικά (foniticá), el estudio de los fenómenos que se dan en la producción del sonido',
    },
  },
  pt: {
    nav: {
      home: 'Início',
      services: 'Áreas',
      team: 'Equipe',
      news: 'Notícias',
      faqs: 'Perguntas',
      contact: 'Contato',
      request: 'Solicitar perícia',
    },
    hero: {
      eyebrow: 'Assunção, Paraguai · Biometria Vocal Forense',
      title: 'Identificação Humana Forense',
      subtitle:
        'Combinamos ciência, tecnologia e precisão para identificar pessoas por sua voz, fala, linguagem e rosto. Confiabilidade sob padrões internacionais (IAFPA, ENFSI, NIST)',
      ctaPrimary: 'Solicitar perícia',
      ctaSecondary: 'Conhecer áreas',
      stats: { years: 'anos de perícia forense', cases: 'perícias realizadas', areas: 'áreas de especialidade' },
      badge1Title: 'Análise acústica forense',
      badge1Sub: 'Comparação de locutores',
      badge2Title: 'Detecção de voz clonada por IA',
      badge2Sub: 'Análise espectral avançada',
      tag1: 'Biometria vocal',
      tag2: 'Perícia forense',
      tag3: 'Detecção de deepfakes',
    },
    services: {
      title: 'Áreas de trabalho',
      subtitle: 'Quatro linhas integradas de identificação humana forense: voz, fala, linguagem e rosto',
      learnMore: 'Ver detalhe',
      sectionTitle: '01',
    },
    team: {
      title: 'Equipe de peritos',
      subtitle: 'Especialistas em fonética forense, linguística forense, tecnologia e biometria facial',
      sectionTitle: '02',
      viewBio: 'Ver biografia',
    },
    news: {
      title: 'Notícias e eventos',
      subtitle: 'Publicações de pesquisa, novidades institucionais e atividades acadêmicas',
      readMore: 'Ler mais',
      by: 'Por',
      sectionTitle: '03',
    },
    faqs: {
      title: 'Perguntas frequentes',
      subtitle: 'Respostas sobre nossos processos e serviços',
    },
    contact: {
      title: 'Contato e solicitação de perícias',
      subtitle: 'Preencha o formulário e um especialista responderá em breve',
      name: 'Nome completo',
      email: 'E-mail',
      phone: 'Telefone',
      subject: 'Assunto',
      message: 'Mensagem',
      submit: 'Enviar mensagem',
      success: 'Sua mensagem foi enviada com sucesso',
      error: 'Não foi possível enviar a mensagem. Tente novamente',
      address: 'Endereço',
      sendUs: 'E-mail',
      sectionTitle: '04',
      judicialNote: 'As solicitações de perícia forense devem anexar a decisão judicial correspondente. Prazo de resposta: 5 dias úteis',
    },
    request: {
      title: 'Solicitação de perícia de voz',
      subtitle: 'Formulário para instituições judiciais, escritórios e particulares',
      requesterName: 'Nome do solicitante',
      institution: 'Instituição / Vara',
      courtCase: 'Processo / Ementa',
      requestType: 'Tipo de solicitação',
      requestTypes: {
        voice: 'Identificação forense de voz',
        audio: 'Análise de áudio',
        video: 'Perícia de áudio e vídeo',
        linguistic: 'Análise de fala e linguagem',
        face: 'Identificação facial forense',
        other: 'Outra',
      },
      email: 'E-mail',
      phone: 'Telefone',
      message: 'Descrição do caso',
      submit: 'Enviar solicitação',
      success: 'Solicitação recebida. Um especialista entrará em contato em breve',
      error: 'Não foi possível processar a solicitação',
      confidential: 'Confidencial',
      confidentialDesc: 'As informações enviadas são tratadas com confidencialidade profissional e cadeia de custódia. Um especialista responsável entrará em contato para coordenar o processo',
      features: [
        'Rastreabilidade documental completa',
        'Laudo pericial assinado digitalmente',
        'Metodologia conforme padrões internacionais',
      ],
    },
    footer: {
      rights: 'Todos os direitos reservados',
      tagline: 'Voz · Fala · Linguagem · Rosto',
      quickLinks: 'Áreas',
      contactUs: 'Contato',
      followUs: 'Redes sociais',
      standards: 'Padrões',
    },
    common: { back: 'Voltar', loading: 'Carregando…', published: 'Publicado', backToNews: 'Voltar às notícias', notFound: 'Não encontrado' },
    a11y: {
      skipToContent: 'Pular para o conteúdo',
      logoHome: 'VPS Laboratório · Ir ao início',
      openMenu: 'Abrir menu',
      closeMenu: 'Fechar menu',
      selectLanguage: 'Selecionar idioma',
      backToTop: 'Voltar ao topo',
      whatsapp: 'Entrar em contato pelo WhatsApp',
      whatsappPrefill: 'Olá, gostaria de informações sobre os serviços da VPS Laboratório',
    },
    offline: {
      title: 'Você está sem conexão',
      message: 'Não foi possível carregar esta página. Verifique sua conexão com a internet e tente novamente',
      hint: 'As páginas que você já visitou continuam disponíveis',
    },
    brand: {
      fullName: 'VPS Laboratório · Biometria Vocal e Identificação Humana Forense',
      shortDescription: 'Identificação humana forense por voz, fala, linguagem e rosto',
      manifesto: 'A ciência que escuta, analisa e revela a verdade',
      etymology: 'Fonética: do grego φωνητικά (foniticá), o estudo dos fenômenos que ocorrem na produção do som',
    },
  },
  en: {
    nav: {
      home: 'Home',
      services: 'Areas',
      team: 'Team',
      news: 'News',
      faqs: 'FAQs',
      contact: 'Contact',
      request: 'Request expertise',
    },
    hero: {
      eyebrow: 'Asunción, Paraguay · Forensic Voice Biometrics',
      title: 'Forensic Human Identification',
      subtitle:
        'We combine science, technology and precision to identify people through their voice, speech, language and face. Reliability backed by international standards (IAFPA, ENFSI, NIST)',
      ctaPrimary: 'Request expertise',
      ctaSecondary: 'Explore areas',
      stats: { years: 'years of forensic expertise', cases: 'forensic cases', areas: 'specialty areas' },
      badge1Title: 'Forensic acoustic analysis',
      badge1Sub: 'Speaker comparison',
      badge2Title: 'AI-cloned voice detection',
      badge2Sub: 'Advanced spectral analysis',
      tag1: 'Vocal biometrics',
      tag2: 'Forensic expertise',
      tag3: 'Deepfake detection',
    },
    services: {
      title: 'Work areas',
      subtitle: 'Four integrated lines of forensic human identification: voice, speech, language and face',
      learnMore: 'Learn more',
      sectionTitle: '01',
    },
    team: {
      title: 'Forensic team',
      subtitle: 'Specialists in forensic phonetics, forensic linguistics, technology and facial biometrics',
      sectionTitle: '02',
      viewBio: 'View biography',
    },
    news: {
      title: 'News and events',
      subtitle: 'Research publications, institutional news and academic activities',
      readMore: 'Read more',
      by: 'By',
      sectionTitle: '03',
    },
    faqs: {
      title: 'Frequently asked questions',
      subtitle: 'Answers about our processes and services',
    },
    contact: {
      title: 'Contact and expertise requests',
      subtitle: 'Fill out the form and a specialist will get back to you shortly',
      name: 'Full name',
      email: 'E-mail address',
      phone: 'Phone',
      subject: 'Subject',
      message: 'Message',
      submit: 'Send message',
      success: 'Your message was sent successfully',
      error: 'We could not send your message. Please try again',
      address: 'Address',
      sendUs: 'E-mail',
      sectionTitle: '04',
      judicialNote: 'Forensic expertise requests must include the corresponding court order. Response time: 5 business days',
    },
    request: {
      title: 'Voice expertise request',
      subtitle: 'Form for courts, law firms and individuals',
      requesterName: 'Requester name',
      institution: 'Institution / Court',
      courtCase: 'Case file / Title',
      requestType: 'Type of request',
      requestTypes: {
        voice: 'Forensic voice identification',
        audio: 'Audio analysis',
        video: 'Audio and video forensics',
        linguistic: 'Speech and language analysis',
        face: 'Forensic face identification',
        other: 'Other',
      },
      email: 'E-mail',
      phone: 'Phone',
      message: 'Case description',
      submit: 'Submit request',
      success: 'Request received. A specialist will contact you shortly',
      error: 'We could not process the request',
      confidential: 'Confidential',
      confidentialDesc: 'All submitted information is treated with professional confidentiality and chain of custody. A responsible specialist will contact you to coordinate the process',
      features: [
        'Complete document traceability',
        'Digitally signed expert report',
        'Methodology aligned with international standards',
      ],
    },
    footer: {
      rights: 'All rights reserved',
      tagline: 'Voice · Speech · Language · Face',
      quickLinks: 'Areas',
      contactUs: 'Contact',
      followUs: 'Social media',
      standards: 'Standards',
    },
    common: { back: 'Back', loading: 'Loading…', published: 'Published', backToNews: 'Back to news', notFound: 'Not found' },
    a11y: {
      skipToContent: 'Skip to content',
      logoHome: 'VPS Laboratorio · Go to homepage',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      selectLanguage: 'Select language',
      backToTop: 'Back to top',
      whatsapp: 'Contact via WhatsApp',
      whatsappPrefill: 'Hello, I would like information about VPS Laboratorio services',
    },
    offline: {
      title: 'You are offline',
      message: 'We could not load this page. Please check your internet connection and try again',
      hint: 'Pages you have already visited remain available',
    },
    brand: {
      fullName: 'VPS Laboratorio · Forensic Voice Biometrics and Human Identification',
      shortDescription: 'Forensic human identification through voice, speech, language and face',
      manifesto: 'The science that listens, analyzes and reveals the truth',
      etymology: 'Phonetics: from the Greek φωνητικά (foniticá), the study of the phenomena occurring in sound production',
    },
  },
};

export function getDictionary(locale: Locale): Dictionary {
  return dictionaries[locale];
}
